import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, Truck, XCircle } from 'lucide-react';
import StaffScanGate from '@/components/StaffScanGate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { JwStatusBadge, fmtDate } from '@/components/tissue/JwStatusBadge';
import { OrderLinesTable } from '@/components/tissue/OrderLinesTable';
import { ReceivePacksCard } from '@/components/tissue/ReceivePacksCard';
import { PackReceiptsCard } from '@/components/tissue/PackReceiptsCard';
import { cancelOrder, closeOrder, errText, getOrder } from '@/services/tissueJwApi';
import { JwEwbBar } from '@/components/jobWork/JwTransfer';

const STEP_NOTE = {
  REQUESTED: 'Waiting for Greige Storage to accept and pick the bundles.',
  PICKING: 'Greige Storage is picking. The Internal Transfer to Tissue is made when picking is done.',
  TRANSFERRED: 'Bundles transferred to Tissue. Make the e-way bill (Job Work DC) and send them through Gate Outward as Job Worker.',
  AT_JOBBER: 'Goods are with the jobber. Receive the FG packs here when they come back.',
  RECEIVING: 'Packs are coming back. Close the order when the jobber has returned everything.',
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg bg-white border px-4 py-3"><p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-2xl font-bold text-[#1A1F2E]" data-testid={testId}>{value}</p></div>
);

const SentBundles = ({ order }) => (
  <Card>
    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Truck className="w-4 h-4" /> Bundles sent ({(order.bundles || []).length})</CardTitle></CardHeader>
    <CardContent className="flex flex-wrap gap-2">
      {(order.bundles || []).map((b) => (
        <span key={b.bundle_code} className="font-mono text-xs border rounded px-2 py-1 bg-white" data-testid={`sent-bundle-${b.bundle_code}`}>
          {b.bundle_code} · {b.sort_no} · {b.pcs} pcs</span>
      ))}
      <p className="w-full text-xs text-gray-500">Internal Transfer {order.transfer_id || '—'}{order.outward_entry_id ? ` · Gate Outward ${order.outward_entry_id}` : ''}</p>
    </CardContent>
  </Card>
);

const CloseBar = ({ order, onDone }) => {
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(order.jw_id, remarks); toast.success(msg); onDone(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const canCancel = ['REQUESTED', 'PICKING'].includes(order.status);
  const canClose = ['TRANSFERRED', 'AT_JOBBER', 'RECEIVING'].includes(order.status);
  const short = (order.sent_pcs || 0) - (order.received_pcs || 0);
  if (!canCancel && !canClose) return null;
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks" className="w-[260px]" data-testid="jw-close-remarks" />
      {canClose && <Button disabled={busy} onClick={() => setConfirm(true)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="jw-close-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Close order</Button>}
      {canCancel && <Button disabled={busy} variant="outline" onClick={() => run(cancelOrder, 'Request cancelled')} className="text-rose-700" data-testid="jw-cancel-btn">
        <XCircle className="w-4 h-4 mr-2" /> Cancel request</Button>}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="jw-close-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Close {order.jw_id}?</AlertDialogTitle>
            <AlertDialogDescription>
              {short > 0 ? `The jobber has returned ${order.received_pcs || 0} of ${order.sent_pcs || 0} pcs — ${short} pcs will be recorded as shortage.`
                : `${order.received_pcs || 0} pcs received against ${order.sent_pcs || 0} sent.`} No more packs can be received after closing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="jw-close-confirm-cancel">Keep open</AlertDialogCancel>
            <AlertDialogAction onClick={() => run(closeOrder, 'Job order closed')} className="bg-emerald-700 hover:bg-emerald-800" data-testid="jw-close-confirm-ok">Close order</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const JwOrderDetail = () => {
  const { jwId } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const load = useCallback(() => getOrder(jwId).then(setO).catch((e) => toast.error(errText(e, 'Job order not found'))), [jwId]);
  useEffect(() => { load(); }, [load]);

  const receivable = o && ['TRANSFERRED', 'AT_JOBBER', 'RECEIVING'].includes(o.status);
  return (
    <StaffScanGate stationName="Tissue — Job Order">
      <div className="min-h-screen bg-[#FFFBF5]" data-testid="tissue-jw-detail">
        <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
          <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/tissue/hub')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="jw-detail-back">
              <ArrowLeft className="w-5 h-5" /></Button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold font-display font-mono" data-testid="jw-detail-id">{jwId}</h1>
              <p className="text-sm text-[#E8DCC8] truncate">{o ? `${o.jobber_name} · raised ${fmtDate(o.created_at)} by ${o.created_staff_name || o.created_by_name || ''}` : 'Loading…'}</p>
            </div>
            {o && <div className="ml-auto"><JwStatusBadge status={o.status} testId="jw-detail-status" /></div>}
          </div>
        </header>
        {o && (
          <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-5">
            {STEP_NOTE[o.status] && <p className="text-sm text-[#2D5F4F] bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-2" data-testid="jw-step-note">{STEP_NOTE[o.status]}</p>}
            <JwEwbBar refNo={o.jw_id} testPrefix="tjw-ewb" />
            {o.reject_reason && <p className="text-sm text-rose-700" data-testid="jw-reject-reason">Rejected by Greige Storage: {o.reject_reason}</p>}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Requested pcs" value={o.requested_pcs || 0} testId="jw-stat-requested" />
              <Stat label="Sent pcs" value={o.sent_pcs || 0} testId="jw-stat-sent" />
              <Stat label="Received pcs" value={o.received_pcs || 0} testId="jw-stat-received" />
              <Stat label={o.status === 'COMPLETED' ? 'Shortage pcs' : 'Packs received'} value={o.status === 'COMPLETED' ? (o.shortage_pcs || 0) : (o.received_packs || 0)} testId="jw-stat-last" />
            </div>
            <Card><CardContent className="pt-6"><OrderLinesTable order={o} /></CardContent></Card>
            {(o.bundles || []).length > 0 && <SentBundles order={o} />}
            {receivable && <ReceivePacksCard order={o} onReceived={load} />}
            {(o.receipts || []).length > 0 && <PackReceiptsCard order={o} />}
            <CloseBar order={o} onDone={load} />
          </main>
        )}
      </div>
    </StaffScanGate>
  );
};

export default JwOrderDetail;
